import { supabase } from '../lib/supabase';
import { getChatCompletion } from './aiIntegrations/chatCompletion';

export const predictiveAnalyticsService = {
  // ============================================================================
  // PREDICTIVE FAILURE DETECTION
  // ============================================================================

  async analyzePredictivePatterns(historicalData) {
    try {
      const messages = [
        {
          role: 'system',
          content: 'You are an expert system reliability engineer. Analyze error patterns and predict potential failures with confidence scores.'
        },
        {
          role: 'user',
          content: `Analyze these system metrics and error patterns to predict potential failures in the next 24-48 hours:\n\n${JSON.stringify(historicalData, null, 2)}\n\nProvide predictions with confidence percentages, severity levels, and recommended preventive actions.`
        }
      ];

      const response = await getChatCompletion(
        'ANTHROPIC',
        'claude-sonnet-4-5-20250929',
        messages,
        {
          temperature: 0.3,
          max_tokens: 2048,
          response_format: {
            type: 'json_schema',
            json_schema: {
              name: 'failure_prediction',
              schema: {
                type: 'object',
                properties: {
                  predictions: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        issue: { type: 'string' },
                        confidence: { type: 'number' },
                        severity: { type: 'string', enum: ['critical', 'high', 'medium', 'low'] },
                        timeframe: { type: 'string' },
                        affectedComponents: { type: 'array', items: { type: 'string' } },
                        preventiveActions: { type: 'array', items: { type: 'string' } }
                      },
                      required: ['issue', 'confidence', 'severity', 'timeframe', 'preventiveActions']
                    }
                  },
                  systemHealthScore: { type: 'number' },
                  riskLevel: { type: 'string' }
                },
                required: ['predictions', 'systemHealthScore', 'riskLevel']
              },
              strict: true
            }
          }
        }
      );

      return JSON.parse(response?.choices?.[0]?.message?.content || '{}');
    } catch (error) {
      console.error('Predictive analysis error:', error);
      throw error;
    }
  },

  async performRootCauseAnalysis(errorData) {
    try {
      const messages = [
        {
          role: 'system',
          content: 'You are an expert system diagnostics engineer. Perform deep root cause analysis and provide human-readable explanations with actionable resolution strategies.'
        },
        {
          role: 'user',
          content: `Analyze this system error and provide root cause analysis:\n\nError Details:\n${JSON.stringify(errorData, null, 2)}\n\nProvide:\n1. Root cause explanation in plain language\n2. Contributing factors\n3. Step-by-step resolution strategy\n4. Prevention recommendations`
        }
      ];

      const response = await getChatCompletion(
        'ANTHROPIC',
        'claude-sonnet-4-5-20250929',
        messages,
        {
          temperature: 0.4,
          max_tokens: 3000,
          response_format: {
            type: 'json_schema',
            json_schema: {
              name: 'root_cause_analysis',
              schema: {
                type: 'object',
                properties: {
                  rootCause: { type: 'string' },
                  contributingFactors: { type: 'array', items: { type: 'string' } },
                  resolutionSteps: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        step: { type: 'number' },
                        action: { type: 'string' },
                        expectedOutcome: { type: 'string' }
                      },
                      required: ['step', 'action', 'expectedOutcome']
                    }
                  },
                  preventionRecommendations: { type: 'array', items: { type: 'string' } },
                  estimatedResolutionTime: { type: 'string' },
                  complexity: { type: 'string', enum: ['low', 'medium', 'high'] }
                },
                required: ['rootCause', 'contributingFactors', 'resolutionSteps', 'preventionRecommendations']
              },
              strict: true
            }
          }
        }
      );

      return JSON.parse(response?.choices?.[0]?.message?.content || '{}');
    } catch (error) {
      console.error('Root cause analysis error:', error);
      throw error;
    }
  },

  async generateSystemHealthForecast(metricsData) {
    try {
      const messages = [
        {
          role: 'system',
          content: 'You are a system health forecasting expert. Analyze current metrics and predict system health trends.'
        },
        {
          role: 'user',
          content: `Based on these system metrics, forecast system health for the next 7 days:\n\n${JSON.stringify(metricsData, null, 2)}\n\nProvide daily health scores, risk indicators, and recommended interventions.`
        }
      ];

      const response = await getChatCompletion(
        'ANTHROPIC',
        'claude-sonnet-4-5-20250929',
        messages,
        {
          temperature: 0.3,
          max_tokens: 2048
        }
      );

      return response?.choices?.[0]?.message?.content || '';
    } catch (error) {
      console.error('Health forecast error:', error);
      throw error;
    }
  },

  // ============================================================================
  // ANOMALY DETECTION
  // ============================================================================

  async detectAnomalies(currentMetrics, historicalBaseline) {
    const anomalies = [];

    // CPU usage anomaly
    if (currentMetrics?.cpuUsage > historicalBaseline?.avgCpuUsage * 1.5) {
      anomalies?.push({
        type: 'cpu_spike',
        severity: 'high',
        message: `CPU usage ${currentMetrics?.cpuUsage}% exceeds baseline by ${((currentMetrics?.cpuUsage / historicalBaseline?.avgCpuUsage - 1) * 100)?.toFixed(1)}%`,
        detectedAt: new Date()?.toISOString()
      });
    }

    // Response time anomaly
    if (currentMetrics?.avgResponseTime > historicalBaseline?.avgResponseTime * 2) {
      anomalies?.push({
        type: 'response_time_degradation',
        severity: 'critical',
        message: `Response time ${currentMetrics?.avgResponseTime}ms is ${((currentMetrics?.avgResponseTime / historicalBaseline?.avgResponseTime - 1) * 100)?.toFixed(1)}% slower than baseline`,
        detectedAt: new Date()?.toISOString()
      });
    }

    // Error rate anomaly
    if (currentMetrics?.errorRate > historicalBaseline?.avgErrorRate * 3) {
      anomalies?.push({
        type: 'error_rate_spike',
        severity: 'critical',
        message: `Error rate ${currentMetrics?.errorRate}% is ${((currentMetrics?.errorRate / historicalBaseline?.avgErrorRate - 1) * 100)?.toFixed(1)}% higher than baseline`,
        detectedAt: new Date()?.toISOString()
      });
    }

    return anomalies;
  },

  // ============================================================================
  // AUTOMATED INTERVENTION
  // ============================================================================

  async executeAutomatedIntervention(interventionType, parameters) {
    const interventions = {
      database_optimization: async () => {
        // Simulate database optimization
        return { success: true, message: 'Database indexes optimized', executedAt: new Date()?.toISOString() };
      },
      cache_clearing: async () => {
        // Simulate cache clearing
        return { success: true, message: 'Application cache cleared', executedAt: new Date()?.toISOString() };
      },
      service_restart: async () => {
        // Simulate service restart
        return { success: true, message: 'Service restarted successfully', executedAt: new Date()?.toISOString() };
      },
      load_balancing: async () => {
        // Simulate load balancing adjustment
        return { success: true, message: 'Load balancing rules updated', executedAt: new Date()?.toISOString() };
      }
    };

    const intervention = interventions?.[interventionType];
    if (!intervention) {
      throw new Error(`Unknown intervention type: ${interventionType}`);
    }

    return await intervention();
  },

  // ============================================================================
  // PREDICTION HISTORY
  // ============================================================================

  async savePrediction(predictionData) {
    const { data, error } = await supabase?.from('predictive_analytics_log')?.insert({
        prediction_type: predictionData?.predictionType,
        confidence_score: predictionData?.confidenceScore,
        severity: predictionData?.severity,
        predicted_issue: predictionData?.predictedIssue,
        timeframe: predictionData?.timeframe,
        affected_components: predictionData?.affectedComponents,
        preventive_actions: predictionData?.preventiveActions,
        system_health_score: predictionData?.systemHealthScore,
        risk_level: predictionData?.riskLevel,
        prediction_metadata: predictionData?.metadata
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  async getPredictionHistory(filters = {}) {
    let query = supabase?.from('predictive_analytics_log')?.select('*')?.order('created_at', { ascending: false });

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }
};

export default predictiveAnalyticsService;
